import { DragEndEvent } from '@dnd-kit/core';
import { createSelector } from '@reduxjs/toolkit';
import { useEffect, useState } from 'react';
import { push } from 'react-router-redux';
import { RootState, useAppDispatch, useAppSelector } from '../../appStore';
import Constants from '../../constants';
import ContentConstants from '../../contentConstants';
import { EditState, ISummaryItem, SurveySummaryItemTypeId } from '../../models';
import { ProgressPanelStatus } from '../../models/ProgressPanelStatus';
import * as surveyApi from '../../services/SurveyAPI';
import { patchSurvey } from '../../services/SurveyAPI';
import { ActionTypes } from '../../store/ActionTypes';
import { ActionCreators as ConfirmScheduleActionCreators } from '../../store/ConfirmScheduleStore';
import { NavigationActions } from '../../store/NavigationActions';
import { loadSurveyTermsAdminData, saveSurveyTermData } from '../../store/SurveyDefinitionsSlice';
import { loadSurveyImage, saveSurveyImage } from '../../store/SurveyImageSlice';
import { loadSurveyIntroAdminData, saveSurveyIntroAdminData } from '../../store/SurveyIntroAdminSlice';
import { savePrivacyPolicyAdminData } from '../../store/SurveyPrivacyPolicySlice';
import { loadSurveyInstructionsAdminData, saveSurveyInstructionsAdminData } from '../../store/SurveyInstructionsAdminSlice';
import { loadSurveyQuestionsAdminData, saveSurveyQuestionAdminData } from '../../store/SurveyQuestionsAdminSlice';
import { ActionCreators } from '../../store/SurveyStore';
import { loadSurveyTheme, saveSurveyTheme } from '../../store/SurveyThemeSlice';
import { loadSurveyTitleAdminData, saveSurveyTitleAdminData } from '../../store/SurveyTitleAdminSlice';
import { loadThemes } from '../../store/ThemeSlice';
import { SurveyContentHelper } from '../SurveyContentHelper';
import { SurveyHelper } from '../SurveyHelper';
import { useBrandingAndTheme } from './useBrandingAndTheme';
import { ProgressPanelHelper } from './useProgressPanelState';

const selectIsAdminEditSurveyDirty = createSelector(
	[
		(state: RootState) => state.surveyTitleAdmin.translations,
		(state: RootState) => state.surveyIntroAdmin.translations,
		(state: RootState) => state.surveyInstructionsAdmin.translations,
		(state: RootState) => state.surveyQuestionsAdmin.questions,
		(state: RootState) => state.surveyQuestionsAdmin.translations,
		(state: RootState) => state.survey.surveyEditModel.isSurveyTermUpdated,
		(state: RootState) => state.survey.surveyEditModel.isPrivacyPolicyUpdated,
		(state: RootState) => state.surveyImage.dirty,
		(state: RootState) => state.surveyTheme.dirty,
		(state: RootState) => state.survey.lastSavedSurveyEditModel.survey.showKincentricLogo,
		(state: RootState) => state.survey.surveyEditModel.survey.showKincentricLogo,
		(state: RootState) => state.survey.lastSavedSurveyEditModel.survey.shouldShowQuestionNumbering,
		(state: RootState) => state.survey.surveyEditModel.survey.shouldShowQuestionNumbering,
		(state: RootState) => state.survey.lastSavedSurveyEditModel.survey.progressMeterType,
		(state: RootState) => state.survey.surveyEditModel.survey.progressMeterType,
		(state: RootState) => state.survey.lastSavedSurveyEditModel.survey.contentItemsOrder,
		(state: RootState) => state.survey.surveyEditModel.survey.contentItemsOrder,
	],
	(
		surveyTitleTranslations,
		surveyIntroTranslations,
		surveyInstructionTranslations,
		surveyQuestions,
		surveyQuestionTranslations,
		isSurveyTermUpdated,
		isPrivacyPolicyUpdated,
		isSurveyImageDirty,
		isSurveyThemeDirty,
		originalShowKincentricLogo,
		showKincentricLogo,
		originalShouldShowQuestionNumbering,
		shouldShowQuestionNumbering,
		originalProgressMeterType,
		progressMeterType,
		originalContentItemsOrder,
		contentItemsOrder,
	) =>
		surveyTitleTranslations.some((t) => t.editState !== EditState.None) ||
		surveyIntroTranslations.some((t) => t.editState !== EditState.None) ||
		surveyInstructionTranslations.some((t) => t.editState !== EditState.None) ||
		surveyQuestions.some((q) => q.editState !== EditState.None) ||
		Object.values(surveyQuestionTranslations).some((values) => values.some((t) => t.editState !== EditState.None)) ||
		isSurveyTermUpdated === true ||
		isPrivacyPolicyUpdated === true ||
		isSurveyImageDirty ||
		isSurveyThemeDirty ||
		originalShowKincentricLogo !== showKincentricLogo ||
		originalShouldShowQuestionNumbering !== shouldShowQuestionNumbering ||
		originalProgressMeterType !== progressMeterType ||
		originalContentItemsOrder !== contentItemsOrder,
);

const selectIsAdminEditSurveyValid = createSelector(
	[
		(state: RootState) => state.surveyTitleAdmin.validationErrors,
		(state: RootState) => state.surveyQuestionsAdmin.validations.errors,
		(state: RootState) => state.surveyImage.validationResult.isValid,
		(state: RootState) => state.surveyTheme.validationResult.isValid,
	],
	(surveyTitleValidationErrors, surveyQuestionValidationErrors, isSurveyImageValid, isSurveyThemeValid) =>
		Object.values(surveyTitleValidationErrors).length === 0 &&
		!Object.values(surveyQuestionValidationErrors).some((errors) => errors.length > 0) &&
		isSurveyImageValid &&
		isSurveyThemeValid,
);

export const useAdminEditSurvey = (surveyId: number) => {
	const dispatch = useAppDispatch();
	const [isLoadingOrSaving, setIsLoadingOrSaving] = useState<boolean>(true);
	const [surveyLanguagesSummaryItem, setSurveyLanguagesSummaryItem] = useState<ISummaryItem | undefined>();

	const { audiences, surveyAdminSummary } = useAppSelector((state) => state.surveyAdmin);

	const openOn = SurveyHelper.getSurveyOpenDate(audiences);
	const closeOn = SurveyHelper.getSurveyCloseDate(audiences);
	const surveyProgressStatus = ProgressPanelHelper.determineStatus(
		undefined,
		openOn,
		closeOn,
		surveyAdminSummary?.surveyStatus,
		audiences,
		surveyAdminSummary?.hasBeenPublished ?? false,
	);

	const isSurveyLive = surveyProgressStatus === ProgressPanelStatus.InProgress;
	const isSurveyClosed = surveyProgressStatus === ProgressPanelStatus.Closed;

	useEffect(() => {
		let ignore = false;

		const load = async () => {
			setIsLoadingOrSaving(true);

			const results = await Promise.allSettled([
				surveyApi.getSurveySummaryItems(surveyId, {
					ignoreValidations: true,
					summaryItemTypes: [SurveySummaryItemTypeId.Languages],
				}),
				dispatch(loadSurveyTitleAdminData(surveyId)).unwrap(),
				dispatch(loadSurveyIntroAdminData(surveyId)).unwrap(),
				dispatch(loadSurveyInstructionsAdminData(surveyId)).unwrap(),
				dispatch(loadSurveyImage(surveyId)).unwrap(),
				dispatch(loadThemes()).unwrap(),
				dispatch(loadSurveyTheme(surveyId)).unwrap(),
				dispatch(loadSurveyQuestionsAdminData(surveyId)).unwrap(),
				// Since this is loading the whole survey not just survey terms, don't need to load privacy policy explicitly
				dispatch(loadSurveyTermsAdminData(surveyId)).unwrap(),
			]);

			if (ignore) {
				return;
			}

			setIsLoadingOrSaving(false);

			if (results.some((result) => result.status === 'rejected')) {
				dispatch({ type: ActionTypes.TOAST_WARNING_NOTIFICATION, payload: ContentConstants.TOAST_ERROR_GENERIC_LOAD_BODY });
			}

			if (results[0].status === 'fulfilled') {
				setSurveyLanguagesSummaryItem(results[0].value[0]);
			}
		};

		void load();

		return () => {
			ignore = true;
		};
	}, [surveyId, dispatch, isSurveyLive]);

	const brandingAndTheme = useBrandingAndTheme();

	const surveyTitleAdmin = useAppSelector((state) => state.surveyTitleAdmin);
	const surveyIntroAdmin = useAppSelector((state) => state.surveyIntroAdmin);
	const surveyInstructionsAdmin = useAppSelector((state) => state.surveyInstructionsAdmin);
	const surveyQuestionsAdmin = useAppSelector((state) => state.surveyQuestionsAdmin);
	const surveyEditModel = useAppSelector((state) => state.survey.surveyEditModel);

	const isPageDirty = useAppSelector(selectIsAdminEditSurveyDirty);
	const isPageValid = useAppSelector(selectIsAdminEditSurveyValid);
	const { openedModalPopupName } = useAppSelector((state: any) => state.surveyContentItems);

	const [activeWarningModal, setActiveWarningModal] = useState<`${'discard-edits' | 'sync-live-survey-edits'}-warning-modal` | undefined>();

	const dismissWarningModal = () => setActiveWarningModal(undefined);

	const cancel = () => {
		if (isPageDirty) {
			setActiveWarningModal('discard-edits-warning-modal');
		} else {
			scrollToTop();
			dispatch(push(`${Constants.ROUTE_SURVEY_ADMIN}/${surveyId}`));
		}
	};

	const confirmCancel = () => dispatch(NavigationActions.Navigate(Constants.ROUTE_SURVEY_ADMIN, surveyId));

	const scrollToTop = () => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

	const save = async () => {
		const {
			survey: { progressMeterType, showKincentricLogo, shouldShowQuestionNumbering, contentItemsOrder },
		} = surveyEditModel;

		scrollToTop();

		setIsLoadingOrSaving(true);

		const results = await Promise.allSettled([
			dispatch(saveSurveyTitleAdminData(surveyId)).unwrap(),
			dispatch(saveSurveyIntroAdminData(surveyId)).unwrap(),
			dispatch(savePrivacyPolicyAdminData(surveyId)).unwrap(),
			dispatch(saveSurveyInstructionsAdminData(surveyId)).unwrap(),
			patchSurvey(surveyId, { progressMeterType, showKincentricLogo, shouldShowQuestionNumbering, contentItemsOrder }),
			dispatch(saveSurveyImage(surveyId)).unwrap(),
			dispatch(saveSurveyTheme(surveyId)).unwrap(),
			dispatch(saveSurveyQuestionAdminData(surveyId)).unwrap(),
			dispatch(saveSurveyTermData(surveyId)).unwrap(),
		]);

		if (results.some((result) => result.status === 'rejected')) {
			setIsLoadingOrSaving(false);
			dispatch({ type: ActionTypes.TOAST_WARNING_NOTIFICATION, payload: ContentConstants.TOAST_ERROR_GENERIC_SAVE_BODY });
			return;
		}

		await dispatch(
			ConfirmScheduleActionCreators.Schedule(
				surveyId,
				push(
					Constants.ROUTE_CONFIRMATION,
					isSurveyLive
						? {
								title: ContentConstants.TITLE_SYNCING_IN_PROGRESS,
								heading: ContentConstants.INSTRUCTIONALTITLE_SURVEY_SYNCING,
								text: ContentConstants.INSTRUCTIONALTEXT_SURVEY_SYNCING,
						  }
						: {
								title: ContentConstants.TITLE_SCHEDULING_IN_PROGRESS,
								heading: ContentConstants.INSTRUCTIONALTITLE_SURVEY_SCHEDULING,
								text: ContentConstants.INSTRUCTIONALTEXT_SURVEY_SCHEDULING,
						  },
				),
			),
		);

		setIsLoadingOrSaving(false);
	};

	const sync = async () => {
		if (isSurveyLive) {
			setActiveWarningModal('sync-live-survey-edits-warning-modal');
		} else {
			await save();
		}
	};

	const confirmSync = async () => {
		dismissWarningModal();
		await save();
	};

	const reSortSurveyContentItem = (event: DragEndEvent) => {
		const newOrderStr = SurveyContentHelper.getNewSortedString(surveyEditModel.survey.contentItemsOrder, event);
		dispatch(ActionCreators.HandleSurveyContentItemSortOrderChange(newOrderStr));
	};
	return {
		isLoadingOrSaving,
		isSurveyLive,
		isSurveyClosed,
		surveyLanguages: surveyLanguagesSummaryItem?.value ?? [],
		isPageDirty,
		isPageValid,
		surveyEditModel,
		surveyTitleAdmin,
		surveyIntroAdmin,
		brandingAndTheme,
		surveyInstructionsAdmin,
		surveyQuestionsAdmin,
		activeWarningModal,
		dismissWarningModal,
		cancel,
		confirmCancel,
		sync,
		confirmSync,
		reSortSurveyContentItem,
		openedModalPopupName,
	};
};
